import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import FormPage from "ui/FormPage";
import FormTextField from "ui/FormTextField";
import { PrimaryButton, OutlineButton } from "ui/Buttons";

import { createGrupo } from "../services/grupos";

const parseErrs = (err) => {
  const out = [];
  const d = err?.response?.data ?? err;
  const push = (m) => m && out.push(String(m));
  if (typeof d === "string") push(d);
  else if (Array.isArray(d)) d.forEach((x) => push(x?.message || JSON.stringify(x)));
  else if (Array.isArray(d?.errors)) d.errors.forEach((x) => push(x?.message || JSON.stringify(x)));
  else if (d?.message) push(d.message);
  else push(err?.message || "Error");
  return out;
};

export default function NuevoGrupo() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  // Nuevo: estado para imagen
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const isValid = useMemo(() => !!name.trim(), [name]);

  // Manejo de selección de archivo + vista previa
  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview("");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  // Limpia el objectURL de la vista previa cuando cambia el archivo o se desmonta
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setSaving(true); setErrors([]);

    try {
      // Enviar como FormData para incluir imagen
      const formData = new FormData();
      formData.append("name", name.trim());
      if (imageFile) formData.append("profile_picture", imageFile);

      await createGrupo(formData); // ver función actualizada abajo
      navigate("/catalogos/grupos", { replace: true });
    } catch (e) {
      setErrors(parseErrs(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPage
      title="Nuevo grupo"
      onBack={() => navigate("/catalogos/grupos")}
      pageMaxWidth={1100}
      contentMaxWidth={900}
      actions={
        <>
          <PrimaryButton type="submit" form="form-grupo" disabled={!isValid || saving}>
            {saving ? "Guardando..." : "Guardar"}
          </PrimaryButton>
          <OutlineButton onClick={() => navigate("/catalogos/grupos")}>Cancelar</OutlineButton>
        </>
      }
    >
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.map((m, i) => <div key={i}>{m}</div>)}
        </Alert>
      )}

      <form id="form-grupo" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormTextField
              label="Nombre*"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </Grid>

          {/* Campo de carga de imagen */}
          <Grid item xs={12} md={6}>
            <Typography sx={{ mb: 1 }}>Imagen (opcional)</Typography>
            <Button variant="outlined" component="label">
              Seleccionar imagen
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={onFileChange}
              />
            </Button>

            {/* Vista previa */}
            {imagePreview && (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                <img
                  src={imagePreview}
                  alt="Vista previa"
                  style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 8 }}
                />
                <Button
                  variant="text"
                  color="error"
                  onClick={() => { setImageFile(null); setImagePreview(""); }}
                >
                  Quitar imagen
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </form>
    </FormPage>
  );
}
